import React from "react";
import { User, Mail, Phone, Calendar, TrendingUp, CheckCircle, XCircle, Clock, Star, Award, ExternalLink } from "lucide-react";

const CandidateView = ({ data }) => {
  if (!data) {
    return null;
  }

  const candidate = Array.isArray(data) ? data[0] : data;
  const initials = `${candidate.first_name?.charAt(0) || ''}${candidate.last_name?.charAt(0) || ''}`.toUpperCase();

  const getStatusConfig = (status) => {
    switch (status) {
      case "ACCEPTED":
        return {
          color: "bg-emerald-100 text-emerald-700 border-emerald-200",
          icon: <CheckCircle size={16} />,
          label: "Accepté"
        };
      case "REJECTED":
        return {
          color: "bg-red-100 text-red-700 border-red-200",
          icon: <XCircle size={16} />,
          label: "Refusé"
        };
      case "PRESELECTED":
        return {
          color: "bg-blue-100 text-blue-700 border-blue-200",
          icon: <Star size={16} />,
          label: "Présélectionné"
        };
      case "INTERVIEW":
        return {
          color: "bg-purple-100 text-purple-700 border-purple-200",
          icon: <Clock size={16} />,
          label: "Entretien"
        };
      default:
        return {
          color: "bg-gray-100 text-gray-700 border-gray-200",
          icon: <Clock size={16} />,
          label: status || "En attente"
        };
    }
  };

  const statusConfig = getStatusConfig(candidate.status);

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-start space-x-4">
        {/* Avatar */}
        <div className="flex-shrink-0">
          <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
            <span className="text-2xl font-bold text-white">
              {initials || candidate.name?.charAt(0)}
            </span>
          </div>
        </div>

        {/* Main Info */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-xl font-bold text-gray-900">
              {candidate.name || `${candidate.first_name} ${candidate.last_name}`}
            </h3>
            <span
              className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center space-x-2 border ${statusConfig.color}`}
            >
              {statusConfig.icon}
              <span>{statusConfig.label}</span>
            </span>
          </div>
          <p className="text-sm text-gray-600 mb-4 flex items-center space-x-2">
            <Award className="w-4 h-4" />
            <span>{candidate.job_offer_title || candidate.position}</span>
          </p>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {candidate.email && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                <Mail className="w-5 h-5 text-blue-600" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                    Email
                  </span>
                  <p className="text-sm font-semibold text-gray-900 truncate">{candidate.email}</p>
                </div>
              </div>
            )}
            {candidate.phone && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                <Phone className="w-5 h-5 text-purple-600" />
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                    Téléphone
                  </span>
                  <p className="text-sm font-semibold text-gray-900">{candidate.phone}</p>
                </div>
              </div>
            )}
            {candidate.application_date && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                <Calendar className="w-5 h-5 text-emerald-600" />
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                    Date de candidature
                  </span>
                  <p className="text-sm font-semibold text-gray-900">
                    {new Date(candidate.application_date).toLocaleDateString("fr-FR", {
                      weekday: 'short',
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </p>
                </div>
              </div>
            )}
            {candidate.matching_score && (
              <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                <TrendingUp className="w-5 h-5 text-teal-600" />
                <div className="flex-1">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider block">
                    Score de matching
                  </span>
                  <div className="flex items-center mt-1">
                    <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                      <div
                        className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2.5 rounded-full transition-all"
                        style={{ width: `${candidate.matching_score}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-purple-600 ml-2 min-w-[50px]">
                      {candidate.matching_score}%
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Action Button */}
      <div className="mt-6 pt-4 border-t border-gray-100">
        <button className="flex items-center space-x-2 text-sm font-medium text-purple-600 hover:text-purple-700 transition-colors">
          <ExternalLink size={16} />
          <span>Voir le profil complet</span>
        </button>
      </div>
    </div>
  );
};

export default CandidateView;
